import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { McpHealthResponse, McpStatusResponse, McpSelfTestResponse, McpOAuthReadinessResponse } from '../lib/api';
import type { SettingsResponse, PaperAccountResponse } from '../types/api';
import { RefreshCw, Check, X, Clock, Shield, Link2, Settings2, Play, Mail, DollarSign, AlertCircle, Unplug, Cpu } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CollapsibleCard } from '../components/CollapsibleCard';
import { Link } from 'react-router-dom';

/**
 * What the rebalancer trades, and at what size.
 *
 * Since SDO-9 every run resolves enabled, allocated DEPLOYMENTS of imported versions — never the
 * `user_symphonies` allocations this card used to list. Showing only those put a deployment enabled
 * at 100% nowhere on the screen that summarises allocations, while listing strategies that no
 * longer trade. So deployments lead, and anything left in the old table is shown for what it is.
 */
function StrategyAllocations({ settings }: { settings: SettingsResponse | null }) {
  const deployments = settings?.deployments ?? [];
  const trading = deployments.filter((d) => d.is_enabled && d.allocation_percent > 0);
  const total = trading.reduce((sum, d) => sum + d.allocation_percent, 0);
  const legacy = (settings?.strategy_allocations ?? []).filter((s) => s.allocation_percent > 0);

  return (
    <div className="space-y-4">
      {deployments.length > 0 ? (
        <>
          <div className="flex justify-between items-center pb-3 border-b border-gray-200">
            <span className="text-gray-600 font-medium">Total allocated (enabled deployments)</span>
            <span className={`text-lg font-bold ${total > 100 ? 'text-red-600' : 'text-emerald-600'}`}>
              {total.toFixed(2)}%
            </span>
          </div>
          {deployments.map((d) => {
            const traded = d.is_enabled && d.allocation_percent > 0;
            return (
              <div key={d.id} className="flex justify-between items-start gap-4 py-3 border-b border-gray-200 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 break-words">{d.name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-semibold text-gray-700 bg-gray-100 rounded px-2 py-0.5">
                      v{d.source_version}
                    </span>
                    <span className={`font-bold rounded px-2 py-0.5 border ${
                      d.trading_mode === 'LIVE'
                        ? 'bg-red-100 text-red-800 border-red-300'
                        : 'bg-blue-100 text-blue-800 border-blue-300'
                    }`}>
                      {d.trading_mode}
                    </span>
                    <span className={`font-bold rounded px-2 py-0.5 border ${
                      d.is_enabled
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                        : 'bg-gray-100 text-gray-600 border-gray-300'
                    }`}>
                      {d.is_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <span className="text-gray-500">
                      {!d.is_enabled
                        ? 'Not traded while disabled'
                        : d.allocation_percent <= 0
                        ? 'Not traded at 0%'
                        : d.trading_mode === 'PAPER'
                        ? 'Trades in PAPER runs; skipped in LIVE runs'
                        : 'Trades in LIVE and PAPER runs'}
                    </span>
                  </div>
                </div>
                <div className={`text-lg font-bold ${traded ? 'text-emerald-700' : 'text-gray-400'}`}>
                  {d.allocation_percent.toFixed(2)}%
                </div>
              </div>
            );
          })}
          <p className="pt-3 border-t border-gray-300 text-sm text-gray-600">
            The rebalancer trades enabled deployments with an allocation, weighted by these
            percentages. Change allocation, mode or enabled on{' '}
            <Link to="/strategies" className="font-semibold text-emerald-700 hover:text-emerald-900 underline">
              Deployed Strategies
            </Link>
            .
          </p>
        </>
      ) : (
        <p className="text-gray-600">
          No deployments yet. Import a version exported from Lab and deploy it on{' '}
          <Link to="/strategies" className="font-semibold text-emerald-700 hover:text-emerald-900 underline">
            Deployed Strategies
          </Link>
          .
        </p>
      )}

      {/* The fallback `fetch_target_weights_for_rebalancing` takes when nothing resolves. */}
      {trading.length === 0 && settings?.symphony_id && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>No enabled deployment:</strong> rebalancing falls back to the Symphony ID set in
            the environment.
          </p>
          <code className="text-xs text-amber-700 font-mono mt-1 block">{settings.symphony_id}</code>
        </div>
      )}

      {legacy.length > 0 && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm font-semibold text-gray-700">Added before Lab import — not traded</p>
          <p className="text-xs text-gray-600 mt-0.5">
            These allocations belong to strategies added directly in Trade. The rebalancer stopped
            reading them when execution was pinned to deployments; to trade one, export it from Lab
            and deploy it.
          </p>
          <ul className="mt-2 space-y-1 text-sm text-gray-600">
            {legacy.map((s) => (
              <li key={s.id} className="flex justify-between gap-4">
                <span className="break-words">{s.name || s.symphony_id}</span>
                <span className="font-mono">{s.allocation_percent.toFixed(2)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function SettingsPage() {
  const { isAdmin, tradingMode } = useAuth();
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [paperAccount, setPaperAccount] = useState<PaperAccountResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingPaperAccount, setLoadingPaperAccount] = useState(false);
  const [resettingPaper, setResettingPaper] = useState(false);
  const [paperResetResult, setPaperResetResult] = useState<{ success: boolean; message: string } | null>(null);
  const [togglingPaperBot, setTogglingPaperBot] = useState(false);
  const [togglingPaperAutoExec, setTogglingPaperAutoExec] = useState(false);
  const [togglingLiveBot, setTogglingLiveBot] = useState(false);
  const [togglingLiveAutoExec, setTogglingLiveAutoExec] = useState(false);
  const [triggeringRun, setTriggeringRun] = useState(false);
  const [runResult, setRunResult] = useState<{ success: boolean; message: string } | null>(null);
  const [resettingBroker, setResettingBroker] = useState(false);
  const [brokerResetResult, setBrokerResetResult] = useState<{ success: boolean; message: string } | null>(null);
  const [mcpHealth, setMcpHealth] = useState<McpHealthResponse | null>(null);
  const [testingMcp, setTestingMcp] = useState(false);
  const [showMcpTools, setShowMcpTools] = useState(false);
  const [mcpStatus, setMcpStatus] = useState<McpStatusResponse | null>(null);
  const [mcpStatusError, setMcpStatusError] = useState<string | null>(null);
  const [mcpSelfTest, setMcpSelfTest] = useState<McpSelfTestResponse | null>(null);
  const [testingMcpHttp, setTestingMcpHttp] = useState(false);
  const [showMcpSelfTestTools, setShowMcpSelfTestTools] = useState(false);
  const [oauthReadiness, setOauthReadiness] = useState<McpOAuthReadinessResponse | null>(null);

  const loadSettings = async () => {
    try {
      const data = await api.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPaperAccount = async () => {
    if (!isAdmin || tradingMode !== 'PAPER') return;
    
    setLoadingPaperAccount(true);
    try {
      const data = await api.getPaperAccount();
      setPaperAccount(data);
    } catch (error) {
      console.error('Error loading paper account:', error);
    } finally {
      setLoadingPaperAccount(false);
    }
  };

  const loadMcpStatus = async () => {
    try {
      const data = await api.getMcpStatus();
      setMcpStatus(data);
      setMcpStatusError(null);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to load MCP status';
      setMcpStatusError(msg);
    }
  };

  const loadOAuthReadiness = async () => {
    try {
      const data = await api.getMcpOAuthReadiness();
      setOauthReadiness(data);
    } catch {
      // non-fatal
    }
  };

  useEffect(() => {
    loadSettings();
    loadPaperAccount();
    if (isAdmin) {
      loadMcpStatus();
      loadOAuthReadiness();
    }
  }, [isAdmin, tradingMode]);

  const handleModeToggle = async (mode: string, field: 'bot_enabled' | 'auto_execute', value: boolean) => {
    if (!settings) return;
    const setLoading = mode === 'PAPER' 
      ? (field === 'bot_enabled' ? setTogglingPaperBot : setTogglingPaperAutoExec)
      : (field === 'bot_enabled' ? setTogglingLiveBot : setTogglingLiveAutoExec);
    setLoading(true);
    try {
      const newSettings = await api.updateModeSettings(
        mode,
        field === 'bot_enabled' ? value : undefined,
        field === 'auto_execute' ? value : undefined
      );
      setSettings(newSettings);
    } catch (error) {
      console.error(`Error toggling ${mode} ${field}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerScheduledRun = async () => {
    if (triggeringRun) return;
    
    setTriggeringRun(true);
    setRunResult(null);
    try {
      const result = await api.triggerScheduledRun();
      setRunResult({ success: true, message: result.message });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setRunResult({ success: false, message: errorMessage });
    } finally {
      setTriggeringRun(false);
    }
  };

  const handlePaperReset = async () => {
    if (resettingPaper) return;
    
    const RESET_AMOUNT = 100000;
    
    // Confirm action with user
    if (!confirm(`Are you sure you want to reset your paper account? This will reset the balance to $${RESET_AMOUNT.toLocaleString()} and delete all positions, orders, and trade history. This action cannot be undone.`)) {
      return;
    }
    
    setResettingPaper(true);
    setPaperResetResult(null);
    try {
      const result = await api.resetPaperAccount(RESET_AMOUNT);
      setPaperResetResult({ success: true, message: result.message || 'Paper account reset successfully' });
      // Reload paper account data
      await loadPaperAccount();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset paper account';
      setPaperResetResult({ success: false, message: errorMessage });
    } finally {
      setResettingPaper(false);
    }
  };

  const handleTestMcp = async () => {
    if (testingMcp) return;
    setTestingMcp(true);
    setMcpHealth(null);
    try {
      const result = await api.testMcpHealth();
      setMcpHealth(result);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Request failed';
      setMcpHealth({
        status: 'unhealthy',
        enabled: true,
        endpoint: '',
        error_type: 'request_error',
        message: msg,
        checked_at: new Date().toISOString(),
      });
    } finally {
      setTestingMcp(false);
    }
  };

  const handleMcpSelfTest = async () => {
    if (testingMcpHttp) return;
    setTestingMcpHttp(true);
    setMcpSelfTest(null);
    try {
      const result = await api.postMcpSelfTest();
      setMcpSelfTest(result);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Request failed';
      setMcpSelfTest({
        status: 'unhealthy',
        tool_count: null,
        tools: [],
        latency_ms: null,
        endpoint: null,
        error: msg,
        checked_at: new Date().toISOString(),
      });
    } finally {
      setTestingMcpHttp(false);
    }
  };

  const handleBrokerReset = async () => {
    if (resettingBroker) return;
    
    if (!confirm('Are you sure you want to reset the Schwab connection? You will need to re-authenticate with Schwab after this.')) {
      return;
    }
    
    setResettingBroker(true);
    setBrokerResetResult(null);
    try {
      const result = await api.resetBrokerConnection();
      setBrokerResetResult({ success: true, message: result.message || 'Broker connection reset successfully' });
      await loadSettings();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset broker connection';
      setBrokerResetResult({ success: false, message: errorMessage });
    } finally {
      setResettingBroker(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-sm text-gray-600 font-medium">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Settings</h1>
        <p className="text-gray-600 mt-1 text-base">View and manage your bot configuration</p>
      </div>

      {/* Per-Mode Bot Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Paper Trading Controls */}
        <CollapsibleCard
          title="Paper Trading"
          icon={<Shield className={`w-5 h-5 ${settings?.paper_bot_enabled ? 'text-blue-600' : 'text-gray-400'}`} />}
          headerExtra={
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ml-2 ${
              settings?.paper_bot_enabled ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {settings?.paper_bot_enabled ? 'Active' : 'Paused'}
            </span>
          }
        >
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <div>
                <span className="text-gray-900 font-bold text-sm">Scheduled Rebalancing</span>
                <p className="text-xs text-gray-600 mt-0.5">
                  {settings?.paper_bot_enabled ? 'Paper bot runs on schedule' : 'Paper bot will not run'}
                </p>
              </div>
              <button
                onClick={() => handleModeToggle('PAPER', 'bot_enabled', !settings?.paper_bot_enabled)}
                disabled={togglingPaperBot}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-sm ${
                  settings?.paper_bot_enabled 
                    ? 'bg-blue-600 focus:ring-blue-600/30' 
                    : 'bg-slate-300 focus:ring-slate-400/30'
                } ${togglingPaperBot ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                  settings?.paper_bot_enabled ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex justify-between items-center py-2">
              <div>
                <span className="text-gray-900 font-bold text-sm">Auto Execute</span>
                <p className="text-xs text-gray-600 mt-0.5">Auto-execute paper orders</p>
              </div>
              <button
                onClick={() => handleModeToggle('PAPER', 'auto_execute', !settings?.paper_auto_execute)}
                disabled={togglingPaperAutoExec}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-sm ${
                  settings?.paper_auto_execute 
                    ? 'bg-amber-500 focus:ring-amber-600/30' 
                    : 'bg-slate-300 focus:ring-slate-400/30'
                } ${togglingPaperAutoExec ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                  settings?.paper_auto_execute ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>
        </CollapsibleCard>

        {/* Live Trading Controls */}
        <CollapsibleCard
          title="Live Trading"
          icon={<AlertCircle className={`w-5 h-5 ${settings?.live_bot_enabled ? 'text-red-600' : 'text-gray-400'}`} />}
          headerExtra={
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ml-2 ${
              settings?.live_bot_enabled ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {settings?.live_bot_enabled ? (
                <><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span> Active</>
              ) : 'Paused'}
            </span>
          }
        >
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <div>
                <span className="text-gray-900 font-bold text-sm">Scheduled Rebalancing</span>
                <p className="text-xs text-gray-600 mt-0.5">
                  {settings?.live_bot_enabled ? 'Live bot runs on schedule' : 'Live bot will not run'}
                </p>
              </div>
              <button
                onClick={() => handleModeToggle('LIVE', 'bot_enabled', !settings?.live_bot_enabled)}
                disabled={togglingLiveBot}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-sm ${
                  settings?.live_bot_enabled 
                    ? 'bg-red-600 focus:ring-red-600/30' 
                    : 'bg-slate-300 focus:ring-slate-400/30'
                } ${togglingLiveBot ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                  settings?.live_bot_enabled ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex justify-between items-center py-2">
              <div>
                <span className="text-gray-900 font-bold text-sm">Auto Execute</span>
                <p className="text-xs text-gray-600 mt-0.5">Auto-execute live orders (real money)</p>
              </div>
              <button
                onClick={() => handleModeToggle('LIVE', 'auto_execute', !settings?.live_auto_execute)}
                disabled={togglingLiveAutoExec}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-sm ${
                  settings?.live_auto_execute 
                    ? 'bg-amber-500 focus:ring-amber-600/30' 
                    : 'bg-slate-300 focus:ring-slate-400/30'
                } ${togglingLiveAutoExec ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                  settings?.live_auto_execute ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {settings?.live_bot_enabled && settings?.live_auto_execute && (
              <div className="p-3 bg-red-50 border border-red-300 rounded-lg">
                <p className="text-xs text-red-800 font-medium">
                  Live auto-execute is ON. Orders will be placed with real money automatically.
                </p>
              </div>
            )}
          </div>
        </CollapsibleCard>
      </div>

      {/* Test Scheduled Run */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-md bg-indigo-600">
            <Play className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Manual Run</h2>
        </div>
        <div className="p-6">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-gray-900 font-bold text-base">Run Rebalance & Execute Orders</span>
              <p className="text-sm text-gray-600 mt-1">Manually trigger the full rebalance job for your current trading mode ({tradingMode}) — generates a plan and auto-executes orders immediately</p>
            </div>
            <button
              onClick={handleTriggerScheduledRun}
              disabled={triggeringRun}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-md ${
                triggeringRun 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 focus:ring-blue-600/30'
              }`}
            >
              {triggeringRun ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Trigger Run
                </>
              )}
            </button>
          </div>
          {runResult && (
            <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${
              runResult.success 
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {runResult.success ? (
                <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" />
              ) : (
                <X className="w-5 h-5 flex-shrink-0 mt-0.5" />
              )}
              <p className="text-sm">{runResult.message}</p>
            </div>
          )}
        </div>
      </div>

      {/* Paper Account Section - Only show for admins in paper mode */}
      {isAdmin && tradingMode === 'PAPER' && (
        <CollapsibleCard title="Paper Trading Account" icon={<DollarSign className="w-5 h-5 text-emerald-600" />}>
          <div className="p-6 space-y-4">
            {loadingPaperAccount ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            ) : paperAccount ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600 font-medium">Starting Cash</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      ${paperAccount.starting_cash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600 font-medium">Current Cash</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      ${paperAccount.current_cash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Reset Paper Account</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Reset account balance to $100,000 and clear all positions, orders, and trade history
                      </p>
                    </div>
                    <button
                      onClick={handlePaperReset}
                      disabled={resettingPaper}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-md whitespace-nowrap ${
                        resettingPaper 
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                          : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-600/30'
                      }`}
                    >
                      {resettingPaper ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4" />
                          Reset Account
                        </>
                      )}
                    </button>
                  </div>
                  {paperResetResult && (
                    <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${
                      paperResetResult.success 
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' 
                        : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                      {paperResetResult.success ? (
                        <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      )}
                      <p className="text-sm">{paperResetResult.message}</p>
                    </div>
                  )}
                </div>

                {paperAccount.reset_at && (
                  <div className="pt-3 text-sm text-gray-500">
                    Last reset: {new Date(paperAccount.reset_at).toLocaleString()}
                  </div>
                )}
              </>
            ) : (
              <div className="py-4 text-center text-gray-500">
                <p>Unable to load paper account information</p>
              </div>
            )}
          </div>
        </CollapsibleCard>
      )}

      <CollapsibleCard title="Broker Configuration" icon={<Link2 className="w-4 h-4 text-emerald-600" />}>
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-600 font-medium">Active Broker</span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">{settings?.active_broker}</span>
          </div>
          {settings?.selected_account_id && (
            <div className="flex justify-between items-center py-2 border-t border-gray-200">
              <span className="text-gray-600 font-medium">Selected Account</span>
              <code className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-mono text-gray-700">{settings.selected_account_id}</code>
            </div>
          )}
          
          {isAdmin && (
            <div className="pt-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-base font-bold text-gray-900">Reset Schwab Connection</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Clear stored tokens and re-authenticate with Schwab
                  </p>
                </div>
                <button
                  onClick={handleBrokerReset}
                  disabled={resettingBroker}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-md whitespace-nowrap ${
                    resettingBroker 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-600/30'
                  }`}
                >
                  {resettingBroker ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Unplug className="w-4 h-4" />
                      Reset Connection
                    </>
                  )}
                </button>
              </div>
              {brokerResetResult && (
                <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${
                  brokerResetResult.success 
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {brokerResetResult.success ? (
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  )}
                  <p className="text-sm">{brokerResetResult.message}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </CollapsibleCard>

      {isAdmin && (
        <CollapsibleCard title="MCP Server" icon={<Cpu className="w-4 h-4 text-violet-600" />}>
          <div className="p-6 space-y-6">

            {/* ── Status summary (auto-loaded on mount) ── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-bold text-gray-900">Status</h3>
                <button
                  onClick={loadMcpStatus}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                  title="Refresh status"
                >
                  <RefreshCw className="w-3 h-3" />
                  Refresh
                </button>
              </div>

              {mcpStatusError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {mcpStatusError}
                </div>
              )}

              {mcpStatus && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 divide-y divide-gray-100">
                  <div className="flex justify-between items-center px-4 py-2 text-sm">
                    <span className="text-gray-600 font-medium">ENABLE_MCP_SERVER</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                      mcpStatus.enable_mcp_server_effective
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {mcpStatus.enable_mcp_server_effective
                        ? <><Check className="w-3 h-3" /> true</>
                        : <><X className="w-3 h-3" /> false</>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-2 text-sm">
                    <span className="text-gray-600 font-medium">App built</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                      mcpStatus.mcp_app_built
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {mcpStatus.mcp_app_built
                        ? <><Check className="w-3 h-3" /> Yes</>
                        : <><X className="w-3 h-3" /> No</>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-2 text-sm">
                    <span className="text-gray-600 font-medium">Mounted at /mcp/</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                      mcpStatus.mcp_mounted
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {mcpStatus.mcp_mounted
                        ? <><Check className="w-3 h-3" /> Yes</>
                        : <><X className="w-3 h-3" /> No</>}
                    </span>
                  </div>
                  {mcpStatus.mcp_mount_path && (
                    <div className="flex justify-between items-center px-4 py-2 text-sm">
                      <span className="text-gray-600 font-medium">Mount path</span>
                      <code className="text-xs font-mono text-gray-700 bg-white px-2 py-0.5 rounded border border-gray-200">
                        {mcpStatus.mcp_mount_path}
                      </code>
                    </div>
                  )}
                  {mcpStatus.message && (
                    <div className="px-4 py-2 text-xs text-gray-500 italic">
                      {mcpStatus.message}
                    </div>
                  )}
                </div>
              )}

              {!mcpStatus && !mcpStatusError && (
                <div className="text-sm text-gray-400 italic">Loading status…</div>
              )}
            </div>

            <div className="border-t border-gray-200" />

            {/* ── In-process check ── */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
                <div>
                  <h3 className="text-base font-bold text-gray-900">In-process check</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Runs initialize + tools/list inside the server process to verify the MCP app is wired up correctly
                  </p>
                </div>
                <button
                  onClick={handleTestMcp}
                  disabled={testingMcp}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-md whitespace-nowrap ${
                    testingMcp
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-violet-600 text-white hover:bg-violet-700 focus:ring-violet-600/30'
                  }`}
                >
                  {testingMcp ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Testing…
                    </>
                  ) : (
                    <>
                      <Cpu className="w-4 h-4" />
                      Run In-process Check
                    </>
                  )}
                </button>
              </div>

              {mcpHealth && (
                <div className={`rounded-lg border p-4 space-y-3 ${
                  mcpHealth.status === 'healthy'
                    ? 'bg-emerald-50 border-emerald-200'
                    : mcpHealth.status === 'degraded'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {mcpHealth.status === 'healthy' ? (
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    ) : mcpHealth.status === 'degraded' ? (
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <span className={`font-bold text-sm ${
                      mcpHealth.status === 'healthy' ? 'text-emerald-700'
                      : mcpHealth.status === 'degraded' ? 'text-amber-700'
                      : 'text-red-700'
                    }`}>
                      {mcpHealth.status.charAt(0).toUpperCase() + mcpHealth.status.slice(1)}
                    </span>
                    {mcpHealth.latency_ms != null && (
                      <span className="ml-auto text-xs text-gray-500">{mcpHealth.latency_ms}ms</span>
                    )}
                  </div>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 font-medium">ENABLE_MCP_SERVER</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                      mcpHealth.enabled
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {mcpHealth.enabled ? <><Check className="w-3 h-3" /> true</> : <><X className="w-3 h-3" /> false</>}
                    </span>
                  </div>

                  {mcpHealth.endpoint && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Endpoint</span>
                      <code className="text-xs font-mono text-gray-700 bg-white px-2 py-0.5 rounded border border-gray-200 max-w-xs truncate">
                        {mcpHealth.endpoint}
                      </code>
                    </div>
                  )}

                  {mcpHealth.transport && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Transport</span>
                      <span className="text-gray-700">{mcpHealth.transport}</span>
                    </div>
                  )}

                  {mcpHealth.tool_count != null && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Tools Available</span>
                      <span className="text-gray-700 font-semibold">{mcpHealth.tool_count}</span>
                    </div>
                  )}

                  {mcpHealth.tool_count != null && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Required Tools Present</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                        mcpHealth.required_tools_present
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {mcpHealth.required_tools_present ? (
                          <><Check className="w-3 h-3" /> Yes</>
                        ) : (
                          <><X className="w-3 h-3" /> No</>
                        )}
                      </span>
                    </div>
                  )}

                  {mcpHealth.missing_required_tools && mcpHealth.missing_required_tools.length > 0 && (
                    <div className="text-sm">
                      <span className="text-red-700 font-medium">Missing required tools:</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {mcpHealth.missing_required_tools.map((t) => (
                          <code key={t} className="text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded">{t}</code>
                        ))}
                      </div>
                    </div>
                  )}

                  {mcpHealth.tools && mcpHealth.tools.length > 0 && (
                    <div className="text-sm border-t border-gray-200 pt-3">
                      <button
                        onClick={() => setShowMcpTools((v) => !v)}
                        className="flex items-center gap-1.5 text-gray-600 font-medium hover:text-gray-900 transition-colors"
                      >
                        <span>{showMcpTools ? '▾' : '▸'}</span>
                        Discovered tools ({mcpHealth.tools.length})
                      </button>
                      {showMcpTools && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {mcpHealth.tools.map((t) => (
                            <code key={t} className="text-xs bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded">{t}</code>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {mcpHealth.message && (
                    <p className={`text-sm ${
                      mcpHealth.status === 'degraded' ? 'text-amber-700' : 'text-red-700'
                    }`}>
                      {mcpHealth.message}
                    </p>
                  )}
                  {mcpHealth.suggested_fix && (
                    <p className="text-xs text-gray-600 italic">{mcpHealth.suggested_fix}</p>
                  )}

                  <p className="text-xs text-gray-400">
                    Checked at {new Date(mcpHealth.checked_at).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200" />

            {/* ── HTTP connectivity check ── */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
                <div>
                  <h3 className="text-base font-bold text-gray-900">HTTP connectivity check</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Makes a real HTTP POST to <code className="font-mono text-xs">/mcp/</code> from the server to confirm the route is properly mounted and reachable
                  </p>
                </div>
                <button
                  onClick={handleMcpSelfTest}
                  disabled={testingMcpHttp}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-md whitespace-nowrap ${
                    testingMcpHttp
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-violet-600 text-white hover:bg-violet-700 focus:ring-violet-600/30'
                  }`}
                >
                  {testingMcpHttp ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Testing…
                    </>
                  ) : (
                    <>
                      <Unplug className="w-4 h-4" />
                      Test HTTP Connectivity
                    </>
                  )}
                </button>
              </div>

              {mcpSelfTest && (
                <div className={`rounded-lg border p-4 space-y-3 ${
                  mcpSelfTest.status === 'healthy'
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {mcpSelfTest.status === 'healthy' ? (
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <span className={`font-bold text-sm ${
                      mcpSelfTest.status === 'healthy' ? 'text-emerald-700' : 'text-red-700'
                    }`}>
                      {mcpSelfTest.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
                    </span>
                    {mcpSelfTest.latency_ms != null && (
                      <span className="ml-auto text-xs text-gray-500">{mcpSelfTest.latency_ms}ms</span>
                    )}
                  </div>

                  {mcpSelfTest.endpoint && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Endpoint tested</span>
                      <code className="text-xs font-mono text-gray-700 bg-white px-2 py-0.5 rounded border border-gray-200 max-w-xs truncate">
                        {mcpSelfTest.endpoint}
                      </code>
                    </div>
                  )}

                  {mcpSelfTest.tool_count != null && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Tools Available</span>
                      <span className="text-gray-700 font-semibold">{mcpSelfTest.tool_count}</span>
                    </div>
                  )}

                  {mcpSelfTest.tools && mcpSelfTest.tools.length > 0 && (
                    <div className="text-sm border-t border-gray-200 pt-3">
                      <button
                        onClick={() => setShowMcpSelfTestTools((v) => !v)}
                        className="flex items-center gap-1.5 text-gray-600 font-medium hover:text-gray-900 transition-colors"
                      >
                        <span>{showMcpSelfTestTools ? '▾' : '▸'}</span>
                        Discovered tools ({mcpSelfTest.tools.length})
                      </button>
                      {showMcpSelfTestTools && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {mcpSelfTest.tools.map((t) => (
                            <code key={t} className="text-xs bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded">{t}</code>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {mcpSelfTest.error && (
                    <p className="text-sm text-red-700">{mcpSelfTest.error}</p>
                  )}

                  <p className="text-xs text-gray-400">
                    Checked at {new Date(mcpSelfTest.checked_at).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>

            {/* ── OAuth Readiness ── */}
            {oauthReadiness && (
              <>
                <div className="border-t border-gray-200" />
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-3">OAuth Readiness (ChatGPT connector)</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Status of the OAuth 2.1 endpoints required for the ChatGPT "Create App" connector.
                    Use <code className="font-mono text-xs bg-gray-100 px-1 rounded">{oauthReadiness.mcp_url}</code> as the MCP Server URL and select <strong>OAuth</strong> as the authentication type.
                  </p>
                  {(() => {
                    const LABELS: Record<string, { label: string; notYet?: boolean }> = {
                      protected_resource_metadata_available: { label: 'Resource metadata endpoint' },
                      authorization_server_metadata_available: { label: 'Auth server metadata endpoint' },
                      authorize_endpoint_available: { label: 'Authorize endpoint' },
                      token_endpoint_available: { label: 'Token endpoint' },
                      mcp_oauth_implementation_ready: { label: 'OAuth implementation ready' },
                      valid_oauth_token_exists: { label: 'Active ChatGPT session', notYet: true },
                      mcp_accepts_bearer_oauth_token: { label: 'MCP server accepts OAuth' },
                    };
                    const hasNoToken = oauthReadiness.checks.valid_oauth_token_exists === false;
                    return (
                      <>
                        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                          {Object.entries(oauthReadiness.checks).map(([key, ok]) => {
                            const meta = LABELS[key];
                            const label = meta?.label ?? key.replace(/_/g, ' ');
                            const isNotYet = !ok && meta?.notYet;
                            return (
                              <div key={key} className="flex justify-between items-center px-4 py-2 text-sm bg-white">
                                <span className="text-gray-600 font-medium">{label}</span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                                  ok
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : isNotYet
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {ok
                                    ? <><Check className="w-3 h-3" /> Ready</>
                                    : isNotYet
                                    ? <>Not yet</>
                                    : <><X className="w-3 h-3" /> Not ready</>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {hasNoToken && (
                          <p className="mt-2 text-xs text-amber-600">
                            No active ChatGPT session yet — this is expected before the first authorization.
                            Connect via ChatGPT to issue a token.
                          </p>
                        )}
                      </>
                    );
                  })()}
                  {oauthReadiness.notes.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {oauthReadiness.notes.map((note, i) => (
                        <li key={i} className="text-xs text-gray-500 flex gap-2">
                          <span className="text-blue-400 flex-shrink-0">→</span>
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}

          </div>
        </CollapsibleCard>
      )}

      <CollapsibleCard title="Strategy Allocations" icon={<Settings2 className="w-4 h-4 text-emerald-600" />}>
        <div className="p-6">
          <StrategyAllocations settings={settings} />
        </div>
      </CollapsibleCard>

      <CollapsibleCard title="Schedule" icon={<Clock className="w-4 h-4 text-amber-600" />}>
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <div>
              <span className="text-gray-700 font-medium">Hourly Preview Schedule</span>
              <p className="text-sm text-gray-600">Fetches target weights and generates preview trades (10AM-3PM EST)</p>
            </div>
            <code className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-mono text-gray-700">{settings?.hourly_rebalance_cron}</code>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <div>
              <span className="text-gray-700 font-medium">Execution Schedule</span>
              <p className="text-sm text-gray-600">Auto-executes trades if enabled (3:50PM EST)</p>
            </div>
            <code className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-mono text-gray-700">{settings?.execution_rebalance_cron}</code>
          </div>
          <div className="flex justify-between items-center py-2">
            <div>
              <span className="text-gray-700 font-medium">Equity Snapshot Schedule</span>
              <p className="text-sm text-gray-600">When portfolio snapshots are taken (4PM EST)</p>
            </div>
            <code className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-mono text-gray-700">{settings?.equity_snapshot_cron}</code>
          </div>
        </div>
      </CollapsibleCard>

      <CollapsibleCard title="Trading Rules" icon={<Shield className="w-4 h-4 text-emerald-600" />}>
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-center py-3 border-b border-gray-200">
            <div>
              <span className="text-gray-700 font-medium">Whole Shares Only</span>
              <p className="text-sm text-gray-600">Only trade whole share quantities</p>
            </div>
            <div className={`flex items-center gap-2 ${settings?.whole_shares_only ? 'text-emerald-600' : 'text-slate-400'}`}>
              {settings?.whole_shares_only ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
              <span className="font-medium">{settings?.whole_shares_only ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          <div className="flex justify-between items-center py-3">
            <div>
              <span className="text-gray-700 font-medium">Allow Short Selling</span>
              <p className="text-sm text-gray-600">Permit selling borrowed shares</p>
            </div>
            <div className={`flex items-center gap-2 ${settings?.allow_shorts ? 'text-amber-600' : 'text-emerald-600'}`}>
              {settings?.allow_shorts ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
              <span className="font-medium">{settings?.allow_shorts ? 'Allowed' : 'Disabled'}</span>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm bg-blue-50 border border-blue-200 text-blue-800">
        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
          <Settings2 className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <p className="font-medium">Configuration via Environment Variables</p>
          <p className="text-sm opacity-80">Settings are configured via environment variables in the Secrets panel.</p>
        </div>
      </div>
    </div>
  );
}
